import { useState, lazy, Suspense } from "react";
import { ChevronRight } from "lucide-react";

// Lazy-load — heavy modal isn't in the home-page bundle, only fetched the first time someone taps "Book Now"
const BookingModal = lazy(() => import("./BookingModal"));

const DAY_INDEX = { Sunday: 0, Monday: 1, Tuesday: 2, Wednesday: 3, Thursday: 4, Friday: 5, Saturday: 6 };

// Compute the next upcoming occurrence of the event's weekday (today counts if it matches).
// Falls back to the event's hardcoded date when day_of_week is missing.
const nextDateFor = (event) => {
  const target = DAY_INDEX[event?.day_of_week];
  if (target == null) return event?.date ? new Date(event.date + "T00:00:00") : null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const offset = (target - today.getDay() + 7) % 7;
  const next = new Date(today);
  next.setDate(today.getDate() + offset);
  return next;
};

const formatDate = (d) => {
  if (!d) return "";
  const weekday = d.toLocaleDateString("en-US", { weekday: "short" });
  const month = d.toLocaleDateString("en-US", { month: "short" });
  const day = d.getDate();
  const suffix = day % 10 === 1 && day !== 11 ? "st" : day % 10 === 2 && day !== 12 ? "nd" : day % 10 === 3 && day !== 13 ? "rd" : "th";
  return `${weekday}, ${month} ${day}${suffix}`;
};

export default function EventCard({ event, index = 0 }) {
  const [open, setOpen] = useState(false);
  const [flipped, setFlipped] = useState(false);
  const hasAlt = !!event.flyer_url_alt;

  return (
    <>
      <div
        className="group flex flex-col bg-black rounded-xl overflow-hidden border border-white/5 hover:border-[#D4AF7A]/50 transition-all duration-500 cursor-pointer h-full"
        data-testid={`event-card-${event.slug}`}
        style={{ animationDelay: `${index * 80}ms` }}
        onClick={() => setOpen(true)}
        onMouseEnter={() => hasAlt && setFlipped(true)}
        onMouseLeave={() => hasAlt && setFlipped(false)}
      >
        {/* Flyer area — flips between primary and alt on hover (3D Y-axis) */}
        <div
          className="relative w-full aspect-[2/3] overflow-hidden bg-black"
          style={{ perspective: "1400px" }}
        >
          <div
            className="absolute inset-0 transition-transform"
            style={{
              transformStyle: "preserve-3d",
              transform: flipped ? "rotateY(180deg)" : "rotateY(0deg)",
              transitionDuration: "900ms",
            }}
          >
            {/* FRONT — primary flyer */}
            <div
              className="absolute inset-0"
              style={{ backfaceVisibility: "hidden", WebkitBackfaceVisibility: "hidden" }}
            >
              <img
                src={event.flyer_url}
                alt={event.title}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-x-0 bottom-0 h-1/4 bg-gradient-to-t from-black/40 to-transparent" />
              {/* Rose-gold flip hint badge — only when alt exists */}
              {hasAlt && (
                <div
                  className="absolute top-3 right-3 z-10 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-black/60 border border-[#D4AF7A]/50 backdrop-blur text-[9px] tracking-[0.22em] uppercase text-[#E8B4B8] pointer-events-none"
                  data-testid={`flip-hint-${event.slug}`}
                >
                  <span className="w-1 h-1 rounded-full bg-[#E8B4B8] animate-pulse" />
                  Flip
                </div>
              )}
            </div>
            {/* BACK — alt flyer */}
            {hasAlt && (
              <div
                className="absolute inset-0"
                style={{
                  backfaceVisibility: "hidden",
                  WebkitBackfaceVisibility: "hidden",
                  transform: "rotateY(180deg)",
                }}
              >
                <img
                  src={event.flyer_url_alt}
                  alt={`${event.title} — alt`}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-x-0 bottom-0 h-1/4 bg-gradient-to-t from-black/40 to-transparent" />
              </div>
            )}
          </div>
        </div>

        {/* Info */}
        <div className="px-4 py-4 flex flex-col gap-3 flex-1">
          <div className="space-y-0.5">
            <div className="text-[10px] tracking-[0.22em] uppercase text-[#D4AF7A]">{formatDate(nextDateFor(event))}</div>
            <h3 className="text-white font-semibold tracking-tight text-sm md:text-[15px] uppercase leading-snug line-clamp-2 min-h-[2.5em]">
              {event.title}
            </h3>
          </div>
          <div className="text-[11px] text-white/55 -mt-1">{event.doors_open} – {event.closes}</div>
          <button
            className="mt-auto w-full flex items-center justify-center gap-1.5 py-2.5 bg-gradient-to-r from-[#D4AF7A] to-[#B76E79] text-black text-[11px] font-semibold tracking-[0.18em] uppercase rounded-full hover:brightness-110 transition-all"
            data-testid={`event-${event.slug}-book`}
          >
            Book Now <ChevronRight size={13} strokeWidth={2.5} />
          </button>
        </div>
      </div>
      {open && (
        <Suspense fallback={null}>
          <BookingModal event={event} onClose={() => setOpen(false)} />
        </Suspense>
      )}
    </>
  );
}
